import { supabase } from '@/lib/supabase'

// CRM data access for the admin tab. Reads go straight to PostgREST — the crm_*
// tables are admin-only by RLS — while anything that sends mail or talks to
// Shopify goes through an edge function so the SendGrid and Shopify keys stay
// server-side.

export const CONSENT_LABELS = {
  opted_in: 'Opted in',
  implied: 'Account',
  none: 'No consent',
}

export const SOURCE_LABELS = {
  newsletter: 'Newsletter',
  app: 'App user',
  shopify: 'Shopify',
  import: 'Import',
}

export const SUPPRESSION_LABELS = {
  unsubscribed: 'Unsubscribed',
  bounced: 'Bounced',
  complained: 'Spam complaint',
  manual: 'Opted out by admin',
}

// Not a consent_status — a pseudo-filter meaning "everyone a campaign sent
// right now would actually reach". Resolved by crm_resolve_audience rather
// than a column match, because being reachable also depends on suppressions
// and the app-user opt-out flag.
export const MAILABLE_FILTER = 'mailable'

// Also a pseudo-filter: contacts with a row in crm_suppressions. Read off the
// crm_contacts_with_suppression view, which joins the two tables.
export const SUPPRESSED_FILTER = 'suppressed'

// The default audience: explicit opt-ins plus app users who haven't opted out.
// Non-consented Shopify buyers are held out until a campaign deliberately
// widens — see the consent model in the CRM schema migration.
export const DEFAULT_AUDIENCE = { consent: ['opted_in', 'implied'], includeNonConsented: false }

async function invoke(fn, body) {
  const { data, error } = await supabase.functions.invoke(fn, { body })
  if (error) {
    // Supabase wraps non-2xx responses; surface the function's own message.
    let parsed = null
    try {
      parsed = await error.context?.json()
    } catch { /* not JSON */ }
    if (parsed?.setupRequired) return { setupRequired: true, error: parsed.error }
    throw new Error(parsed?.error || error.message || 'Request failed')
  }
  if (data?.setupRequired) return { setupRequired: true, error: data.error }
  if (data?.error) throw new Error(data.error)
  return data
}

// The PostgREST builder is thenable, not a resolved object — it has to be
// awaited before there is an `error` to read. Destructuring it directly reads
// undefined and swallows every failure silently.
async function unwrap(query) {
  const { data, error } = await query
  if (error) throw new Error(error.message)
  return data
}

// ------------------------------------------------------------------ contacts

const CONTACT_COLUMNS =
  'id, email, first_name, last_name, sources, consent_status, consent_source, user_id, shopify_customer_id, tags, created_at'

export async function fetchContacts({ search = '', source = '', consent = '', page = 0, pageSize = 50 } = {}) {
  const mailableOnly = consent === MAILABLE_FILTER
  const suppressedOnly = consent === SUPPRESSED_FILTER

  // The mailable list comes from the resolver, not a consent_status match, so
  // that "who can I reach" here is the same set crm-send would mail — a column
  // filter would wrongly include suppressed addresses and opted-out app users.
  // PostgREST filters set-returning functions the same way it filters tables,
  // so search, source and paging below apply to either source unchanged.
  //
  // Everything else reads the view rather than the table: it carries each
  // contact's suppression reason, which the list shows as a badge and the
  // "suppressed" filter matches on. The resolver already excludes suppressed
  // addresses, so its rows never need the column.
  let query = mailableOnly
    ? supabase
        .rpc('crm_resolve_audience', { p_audience: DEFAULT_AUDIENCE }, { count: 'exact' })
        .select(CONTACT_COLUMNS)
    : supabase
        .from('crm_contacts_with_suppression')
        .select(`${CONTACT_COLUMNS}, suppression_reason`, { count: 'exact' })

  query = query
    .order('created_at', { ascending: false })
    .range(page * pageSize, page * pageSize + pageSize - 1)

  if (search.trim()) {
    const term = `%${search.trim()}%`
    query = query.or(`email.ilike.${term},first_name.ilike.${term},last_name.ilike.${term}`)
  }
  if (source) query = query.contains('sources', [source])
  if (suppressedOnly) query = query.not('suppression_reason', 'is', null)
  else if (consent && !mailableOnly) query = query.eq('consent_status', consent)

  const { data, error, count } = await query
  if (error) throw new Error(error.message)
  return { contacts: data ?? [], total: count ?? 0 }
}

// Counts for the header. Cheap enough as four head-only queries and it avoids
// a bespoke RPC for something this simple.
export async function fetchContactStats() {
  const countOf = async (build) => {
    const { count, error } = await build(
      supabase.from('crm_contacts').select('id', { count: 'exact', head: true }),
    )
    if (error) throw new Error(error.message)
    return count ?? 0
  }

  const [total, optedIn, implied, none, suppressed, mailable] = await Promise.all([
    countOf((q) => q),
    countOf((q) => q.eq('consent_status', 'opted_in')),
    countOf((q) => q.eq('consent_status', 'implied')),
    countOf((q) => q.eq('consent_status', 'none')),
    // Counted off the same view the "suppressed" filter lists, not
    // crm_suppressions itself: a suppression outlives its contact, so the raw
    // table can hold addresses the contact list will never show.
    supabase.from('crm_contacts_with_suppression').select('id', { count: 'exact', head: true })
      .not('suppression_reason', 'is', null)
      .then(({ count, error }) => { if (error) throw new Error(error.message); return count ?? 0 }),
    audienceCount(DEFAULT_AUDIENCE),
  ])

  return { total, optedIn, implied, none, suppressed, mailable }
}

// The one place that answers "how many people would this actually reach".
// Shared with crm-send so the number shown is the number mailed.
//
// Deliberately NOT `head: true`. That makes supabase-js send a HEAD request
// with the arguments in the query string, where the jsonb filter is coerced by
// String() into the literal text "[object Object]" and PostgREST rejects the
// call with a 400. The count has to come from a POST, which sends the filter
// as a JSON body. `limit(1)` keeps the response tiny — PostgREST still reports
// the full total in Content-Range.
export async function audienceCount(audience) {
  const { count, error } = await supabase
    .rpc('crm_resolve_audience', { p_audience: audience ?? {} }, { count: 'exact' })
    .select('id')
    .limit(1)
  if (error) throw new Error(error.message)
  return count ?? 0
}

// CSV import. Rows are upserted one at a time through crm_upsert_contact so
// the consent ratchet and source merging apply to imports exactly as they do
// to a sync — an import can never demote someone's existing consent.
export async function importContacts(rows, { source = 'import', consent = 'none', consentSource = 'CSV import', tags = [] } = {}) {
  const results = { imported: 0, failed: 0, errors: [] }

  for (const row of rows) {
    const { error } = await supabase.rpc('crm_upsert_contact', {
      p_email: row.email,
      p_source: source,
      p_consent_status: consent,
      p_consent_source: consentSource,
      p_first_name: row.first_name ?? null,
      p_last_name: row.last_name ?? null,
      p_tags: tags,
    })
    if (error) {
      results.failed += 1
      if (results.errors.length < 10) results.errors.push(`${row.email}: ${error.message}`)
    } else {
      results.imported += 1
    }
  }
  return results
}

export const syncContacts = (sources) => invoke('crm-sync', { sources })

// -------------------------------------------------------------- suppressions

export async function fetchSuppressions({ search = '', reason = '', page = 0, pageSize = 50 } = {}) {
  let query = supabase
    .from('crm_suppressions')
    .select('id, email, reason, detail, campaign_id, created_at', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(page * pageSize, page * pageSize + pageSize - 1)

  if (search.trim()) query = query.ilike('email', `%${search.trim()}%`)
  if (reason) query = query.eq('reason', reason)

  const { data, error, count } = await query
  if (error) throw new Error(error.message)
  return { suppressions: data ?? [], total: count ?? 0 }
}

// ignoreDuplicates: an address that is already suppressed keeps its existing
// row. Otherwise an admin re-adding a bounced address would rewrite the reason
// to 'manual', which the UI treats as liftable — a back door out of the mail
// provider's verdict.
export const addSuppressions = (emails, detail = 'Added by an admin') => {
  const rows = [...new Set(emails.map((e) => e.trim().toLowerCase()).filter(Boolean))]
    .map((email) => ({ email, reason: 'manual', detail }))
  if (rows.length === 0) return Promise.resolve(null)
  return unwrap(
    supabase.from('crm_suppressions')
      .upsert(rows, { onConflict: 'email', ignoreDuplicates: true }),
  )
}

export const addSuppression = (email, detail) => addSuppressions([email], detail)

// Only ever used on suppressions an admin created by hand. Bounces and spam
// complaints are the mailbox provider's verdict and the UI doesn't offer to
// lift them.
export const removeSuppression = (id) =>
  unwrap(supabase.from('crm_suppressions').delete().eq('id', id))

// ----------------------------------------------------------------- campaigns

export async function fetchCampaigns() {
  const { data, error } = await supabase
    .from('crm_campaigns')
    .select('id, name, subject, status, recipient_count, sent_count, failed_count, test_sent_at, sent_at, created_at, audience')
    .order('created_at', { ascending: false })
  if (error) throw new Error(error.message)
  return data ?? []
}

export async function fetchCampaign(id) {
  const { data, error } = await supabase.from('crm_campaigns').select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(error.message)
  return data
}

export async function createCampaign(fields) {
  const { data, error } = await supabase
    .from('crm_campaigns')
    .insert({ audience: DEFAULT_AUDIENCE, ...fields })
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data
}

export async function updateCampaign(id, fields) {
  const { data, error } = await supabase
    .from('crm_campaigns')
    .update(fields)
    .eq('id', id)
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data
}

export const deleteCampaign = (id) =>
  unwrap(supabase.from('crm_campaigns').delete().eq('id', id))

export const sendTestEmail = (campaignId, to) => invoke('crm-send', { action: 'test', campaignId, to })
export const sendCampaign = (campaignId) => invoke('crm-send', { action: 'send', campaignId })
export const cancelCampaign = (campaignId) => invoke('crm-send', { action: 'cancel', campaignId })

// ------------------------------------------------------------------- results

// Per-campaign tallies. Opens are reported but deliberately labelled as
// unreliable in the UI: Apple Mail Privacy Protection fires open pixels
// without anyone reading anything, so clicks are the honest engagement number.
export async function fetchCampaignResults(campaignId) {
  const { data: events, error } = await supabase
    .from('crm_email_events')
    .select('event, email')
    .eq('campaign_id', campaignId)
  if (error) throw new Error(error.message)

  const uniqueBy = (name) => new Set(events.filter((e) => e.event === name).map((e) => e.email)).size

  return {
    delivered: uniqueBy('delivered'),
    opened: uniqueBy('open'),
    clicked: uniqueBy('click'),
    bounced: uniqueBy('bounce') + uniqueBy('dropped'),
    complained: uniqueBy('spamreport'),
    unsubscribed: uniqueBy('unsubscribe') + uniqueBy('group_unsubscribe'),
    totalEvents: events.length,
  }
}

export async function fetchCampaignFailures(campaignId, limit = 25) {
  const { data, error } = await supabase
    .from('crm_campaign_sends')
    .select('email, status, error')
    .eq('campaign_id', campaignId)
    .in('status', ['failed', 'skipped'])
    .limit(limit)
  if (error) throw new Error(error.message)
  return data ?? []
}
